import { Box, Chip, Divider, Typography } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import { formatDateBR, formatMoney, formatPercent } from '../../../utils';
import { useTranslation } from '../../../hooks/useTranslation';
import CustomTable from '../../../components/CustomTable';

const STATUS_CONFIG = {
  PENDING: { color: '#fff8e1', textColor: '#f57f17' },
  APPROVED: { color: '#e8f5e9', textColor: '#2e7d32' },
  REJECTED: { color: '#fce4ec', textColor: '#c62828' },
};

const TYPE_CONFIG = {
  REVENDA: { label: 'Revenda', color: '#E7F0FE', textColor: '#3C83F6' },
  REDUTO_CAPITAL: { label: 'Reduto Capital', color: '#e3f2fd', textColor: '#1565c0' },
};

const CHANGE_TYPE_CONFIG = {
  CRIACAO: { label: 'Criação', color: '#DCE7FC', textColor: '#33528B' },
  AJUSTE: { label: 'Ajuste', color: '#FCF1DC', textColor: '#977330' },
};

const StatusChip = ({ status, label }) => {
  const config = STATUS_CONFIG[status] ?? { color: '#f5f5f5', textColor: '#757575' };
  return (
    <Chip
      label={label ?? status ?? '-'}
      size="small"
      sx={{
        backgroundColor: config.color,
        color: config.textColor,
        fontWeight: 700,
        fontSize: '11px',
        borderRadius: '6px',
        height: '22px',
      }}
    />
  );
};

const InfoField = ({ label, value, highlight = false }) => (
  <Box>
    <Typography
      sx={{
        fontSize: '11px',
        fontWeight: 600,
        color: '#9e9e9e',
        textTransform: 'uppercase',
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: '16px', fontWeight: 600, color: highlight ? '#2e7d32' : '#212121' }}
    >
      {value ?? '-'}
    </Typography>
  </Box>
);

const DadosSimulacaoTab = ({ simulation }) => {
  const { t } = useTranslation();

  const STAGE_DESCRIPTION = {
    PENDING: t('simulation.adm.detail.statusCreated'),
    APPROVED: t('simulation.adm.detail.statusApproved'),
    REJECTED: t('simulation.adm.detail.statusRejected'),
  };

  const STATUS_LABEL = {
    PENDING: t('simulation.adm.detail.statusPending'),
    APPROVED: t('simulation.approved'),
    REJECTED: t('simulation.failed'),
  };

  const changeColumns = [
    {
      field: 'type',
      headerName: t('simulation.type'),
      render: (value) => {
        const config = CHANGE_TYPE_CONFIG[value] ?? {
          label: value ?? '-',
          color: '#f5f5f5',
          textColor: '#757575',
        };
        return (
          <Chip
            label={config.label}
            size="small"
            sx={{
              backgroundColor: config.color,
              color: config.textColor,
              fontWeight: 700,
              fontSize: '11px',
              borderRadius: '6px',
              height: '22px',
            }}
          />
        );
      },
    },
    {
      field: 'total_value',
      headerName: t('simulation.value'),
      render: (value) => formatMoney(value),
    },
    {
      field: 'installments',
      headerName: t('simulation.installments'),
      render: (value) => (value ? `${value}x` : '-'),
    },
    {
      field: 'createdAt',
      headerName: t('simulation.date'),
      render: (value) => formatDateBR(value, false),
    },
  ];

  const typeConfig = TYPE_CONFIG[simulation?.type] ?? {
    label: simulation?.type ?? '-',
    color: '#f5f5f5',
    textColor: '#757575',
  };

  const statusHistory = (() => {
    if (!simulation) return [];
    const actor = simulation.client_name
      ? { name: simulation.client_name, company: simulation.company?.name ?? null }
      : null;
    const entries = [
      {
        stage: 'PENDING',
        description: STAGE_DESCRIPTION.PENDING,
        date: simulation.created_at,
        actor,
      },
    ];
    if (simulation.stage !== 'PENDING') {
      entries.push({
        stage: simulation.stage,
        description: STAGE_DESCRIPTION[simulation.stage] ?? simulation.stage,
        date: simulation.updated_at,
        actor,
      });
    }
    return entries;
  })();

  const rawChangeHistory = simulation?.custom_fields?.changeHistory ?? [];
  const changeHistory =
    rawChangeHistory.length > 0
      ? rawChangeHistory
      : simulation
        ? [
            {
              type: 'CRIACAO',
              total_value: simulation.total_value,
              installments: simulation.installments,
              installment_value: simulation.installment_value,
              createdAt: simulation.created_at,
            },
          ]
        : [];

  const changeRows = changeHistory.map((row, idx) => ({ ...row, _key: idx }));

  const customDates = Array.isArray(simulation?.custom_dates) ? simulation.custom_dates : [];
  const hasCustomDueDates = simulation?.payment_type === 'data_personalizada';
  const showInstallmentsTable = hasCustomDueDates && customDates.length > 1;

  const installmentColumns = [
    {
      field: 'number',
      headerName: t('simulation.adm.detail.installmentsTable.number'),
      render: (value) => `${value}/${customDates.length}`,
    },
    {
      field: 'dueDate',
      headerName: t('simulation.adm.detail.installmentsTable.dueDate'),
      render: (value) => formatDateBR(value, false),
    },
    {
      field: 'value',
      headerName: t('simulation.adm.detail.installmentsTable.value'),
      render: (value) => formatMoney(value),
    },
  ];

  const installmentRows = customDates.map((item, idx) => ({
    _key: idx,
    number: idx + 1,
    value: item?.value,
    dueDate: item?.date,
  }));

  return (
    <Box>
      {/* Tipo de financiamento */}
      <Box mb={3} sx={{ border: '2px solid #E5E7EB', padding: '16px', borderRadius: '8px' }}>
        <Box mb={3}>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#9e9e9e',
              textTransform: 'uppercase',
              mb: 1.5,
            }}
          >
            {t('simulation.adm.detail.financingType')}
          </Typography>
          <Chip
            label={typeConfig.label}
            size="small"
            sx={{
              backgroundColor: typeConfig.color,
              color: typeConfig.textColor,
              fontWeight: 700,
              fontSize: '12px',
              borderRadius: '6px',
              height: '24px',
            }}
          />
        </Box>

        {/* Valores financeiros */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 3,
            mb: 3,
          }}
        >
          <InfoField
            label={t('simulation.totalValue')}
            value={formatMoney(simulation?.total_value)}
          />
          <InfoField
            label={t('simulation.adm.detail.entryValue')}
            value={formatMoney(simulation?.entry_value)}
          />
          <InfoField
            label={t('simulation.loanValue')}
            value={formatMoney(simulation?.financed_value)}
            highlight
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 3,
            mb: 3,
          }}
        >
          <InfoField
            label={t('simulation.installments')}
            value={simulation?.installments ? `${simulation.installments}x` : '-'}
          />
          <InfoField
            label={t('simulation.ValueInstallment')}
            value={formatMoney(simulation?.installment_value)}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 3,
            mb: 3,
          }}
        >
          <InfoField
            label={t('simulation.adm.detail.monthlyInterest')}
            value={
              simulation?.interest_rate != null
                ? `${formatPercent(simulation.interest_rate)}%`
                : '-'
            }
          />
          <InfoField
            label={t('simulation.adm.detail.iof')}
            value={`${formatPercent(simulation?.iof_rate)}%`}
          />
          <InfoField
            label={t('simulation.adm.detail.tac')}
            value={`${formatPercent(simulation?.tac_rate)}%`}
          />
          <InfoField
            label={t('simulation.operational')}
            value={formatMoney(simulation?.operational_fee)}
          />
        </Box>

        {/* Detalhamento das parcelas — vencimento personalizado */}
        {showInstallmentsTable && (
          <Box sx={{ '& tbody tr:last-child td': { borderBottom: 'none' } }}>
            <Divider sx={{ mt: 4, mb: 2 }} />
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#9e9e9e',
                textTransform: 'uppercase',
                mb: 1.5,
              }}
            >
              {t('simulation.adm.detail.installmentsTable.title')}
            </Typography>
            <CustomTable
              columns={installmentColumns}
              rows={installmentRows}
              rowKey="_key"
              hidePagination
              emptyMessage={t('simulation.adm.detail.installmentsTable.empty')}
              elevation={0}
            />
          </Box>
        )}
      </Box>

      {/* Histórico de Status */}
      <Box mb={3} sx={{ border: '2px solid #E5E7EB', padding: '16px', borderRadius: '8px' }}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            color: '#9e9e9e',
            textTransform: 'uppercase',
            mb: 2,
          }}
        >
          {t('simulation.adm.detail.statusHistory')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {statusHistory.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                py: 1,
                flexDirection: {
                  xs: 'column',
                  md: 'row',
                },
              }}
            >
              <StatusChip status={item.stage} label={STATUS_LABEL[item.stage]} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#212121' }}>
                  {item.description}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 0.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: '#6B7280' }} />
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                      {formatDateBR(item.date)}
                    </Typography>
                  </Box>
                  {item.actor?.name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonOutlineIcon sx={{ fontSize: 13, color: '#6B7280' }} />
                      <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                        {item.actor.name}
                        {item.actor.company ? ` (${item.actor.company})` : ''}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Histórico de Alterações */}
      <Box sx={{ border: '2px solid #E5E7EB', padding: '16px', borderRadius: '8px' }}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            color: '#9e9e9e',
            textTransform: 'uppercase',
            mb: 2,
          }}
        >
          {t('simulation.adm.detail.changeHistory')}
        </Typography>

        <CustomTable
          columns={changeColumns}
          rows={changeRows}
          rowKey="_key"
          rowsPerPageOptions={[5, 10]}
          emptyMessage={t('simulation.adm.detail.noChangesFound')}
          elevation={0}
        />
      </Box>
    </Box>
  );
};

export default DadosSimulacaoTab;
